/**
 * `theme/assets/fit-profile.js` — DOM and network layer for the fit-profile
 * panel: a once-per-purchase height/weight collector shared across the cart
 * and mirrored onto every line, so the workroom knows which body each piece
 * was cut to.
 *
 * Holds the shared profile (`fitProfile`), renders/validates the panel markup
 * (`theme/snippets/fit-profile-panel.liquid`, rendered by the fit-profile block
 * and by `theme/snippets/quick-add-modal.liquid`), and gates every add path on
 * a profile existing — see the interceptor at the foot of this file.
 * Units/bounds are pure and live in `fit-profile-units.js`.
 *
 * Three add paths, one gate:
 * - the product page's own buy button, and the quick-add form inside the
 *   quick-add modal — both are `product-form-component form`, so both are
 *   caught by the single window-capture `submit` listener below with no
 *   path-specific code;
 * - a quick-add card's "Add" button on a collection or recommendations grid —
 *   the same selector again, but with no panel in its own section, so it
 *   answers to the shared panel (`panelFor`);
 * - accelerated checkout, which cannot be intercepted at all because it skips
 *   the cart entirely. It is hidden until a profile exists instead — see
 *   `[data-fit-gate]` at the foot of this file.
 */

import {
  unitSystemForCountry,
  normalizeFitFields,
  mirrorFitFields,
  decideSubmitAction,
  resolveFitPanel,
  panelIsVisible,
  fieldStateFor,
  lineSourceFor,
  clearedLineDecision,
  convertedHintFor,
  fittingToSentence,
  measurementNumber,
} from '@theme/fit-profile-units';
import { reconcileLines } from '@theme/fit-profile-reconcile';
import { sectionRenderer } from '@theme/section-renderer';

const UNIT_STORAGE_KEY = 'tiffany:fit-units';

/** The literal sentinel written for a gifted piece whose measurements are unknown. */
const TO_CONFIRM = 'To confirm with customer';

/** Copy from the design spec's Failure modes table, verbatim. */
const SAVE_FAILED_MESSAGE = "We couldn't save that — try again.";

/** localStorage throws in some private-browsing modes. Never let that break the page. */
function readStoredUnits() {
  try {
    return window.localStorage.getItem(UNIT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredUnits(system) {
  try {
    window.localStorage.setItem(UNIT_STORAGE_KEY, system);
  } catch {
    // Preference is a convenience. The profile itself lives in cart attributes.
  }
}

/** The shared, cart-level fit profile — one instance for the whole page. Every
 * `<fit-profile>` element reads/writes through it and re-renders via `subscribe`. */
class FitProfile {
  /** @type {{height: string, weight: string}} */
  #state;
  /** @type {Set<() => void>} */
  #listeners = new Set();

  /** @param {{height?: string, weight?: string, country?: string}} bootstrap */
  constructor(bootstrap) {
    this.#state = {
      height: bootstrap.height || '',
      weight: bootstrap.weight || '',
    };
    /** ISO 3166-1 alpha-2, from `localization.country.iso_code`. */
    this.country = bootstrap.country || '';
  }

  /** @returns {'metric'|'imperial'} Stored preference first, then country. */
  get unitSystem() {
    return readStoredUnits() || unitSystemForCountry(this.country);
  }

  /** @param {'metric'|'imperial'} system */
  set unitSystem(system) {
    writeStoredUnits(system);
    this.#emit();
  }

  /** @returns {boolean} Whether a cart-level profile exists yet. */
  isSet() {
    return Boolean(this.#state.height && this.#state.weight);
  }

  /** @returns {{height: string, weight: string}} A copy — never the live state. */
  get() {
    return { ...this.#state };
  }

  /**
   * Build the line-item properties for one cart line. `'to_confirm'` never
   * touches the profile's real values — both fields carry the literal
   * sentinel, so a gifted line can never be cut to the wrong body.
   * `'inherited'` and `'own'` both carry the profile's current values (they
   * are numerically identical at the moment a line is added); the
   * leading-underscore key is what a later edit reads to decide whether this
   * line may be rewritten. This method does not decide which source
   * applies — the add-to-cart caller does, by reading the relevant
   * `<fit-profile>` element's `lineSource` getter.
   *
   * @param {'inherited'|'own'|'to_confirm'} source
   * @returns {{Height: string, Weight: string, _fit_source: string}}
   */
  properties(source) {
    if (source === 'to_confirm') {
      return { Height: TO_CONFIRM, Weight: TO_CONFIRM, _fit_source: 'to_confirm' };
    }
    return { Height: this.#state.height, Weight: this.#state.weight, _fit_source: source };
  }

  /**
   * Writes the cart-level profile. Callers must validate first — this does
   * no validation and expects the exact `value` strings `normalizeHeight`/
   * `normalizeWeight` produced.
   * @param {{height: string, weight: string}} values
   */
  async save({ height, weight }) {
    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          'Fit height': height,
          'Fit weight': weight,
        },
      }),
    });
    if (!response.ok) throw new Error('Could not save the fit profile');
    this.#state = { height, weight };
    this.#emit();
  }

  /**
   * @param {() => void} listener
   * @returns {() => void} Unsubscribe.
   */
  subscribe(listener) {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit() {
    for (const listener of this.#listeners) listener();
  }
}

export const fitProfile = new FitProfile(window.fitProfileBootstrap || {});

/**
 * Puts the question on screen and waits for the customer to settle it — by
 * saving measurements, confirming the "ask me" gift box, or pressing the
 * panel's own button to accept the numbers it already shows. A page with no
 * panel must never become unshoppable, so `null` resolves `true` unasked.
 *
 * It used to open `if (!fitProfile.isSet())` and short-circuit `true`
 * otherwise. That was the same conflation Addendum 8 corrects one layer up: a
 * profile existing is not the same as the customer having been shown it. Left
 * in, it would have made the new `gate` a silent no-op — the interceptor would
 * decide to ask, this would answer for the customer, and the line would be
 * added `inherited` with nothing on screen, exactly as before the fix.
 *
 * There is now ONE place that decides whether to ask, `decideSubmitAction`,
 * and this is only called on its `gate` branch. So this asks, unconditionally.
 *
 * The panel is a parameter, not a lookup. It used to be
 * `document.querySelector('fit-profile')` — a page-wide search, while the
 * properties that end up on the line came from a section-scoped one. Two
 * lookups, two answers, and on a collection page they are not the same
 * element. The caller now resolves the panel once (`panelFor`) and hands the
 * same one to this and to `injectProperties`.
 *
 * @param {{openAndAwait: () => Promise<boolean>} | null} panel
 * @returns {Promise<boolean>}
 */
export function requireProfile(panel) {
  if (!panel) return Promise.resolve(true); // no panel here: do not block the sale
  return panel.openAndAwait();
}

// ---------------------------------------------------------------------------
// <fit-profile> element
// ---------------------------------------------------------------------------

/** @param {Element} root @param {string} name @returns {HTMLElement | null} */
function refIn(root, name) {
  return root.querySelector(`[data-ref="${name}"]`);
}

class FitProfileElement extends HTMLElement {
  /** @type {AbortController | null} Bound listeners; abort on disconnect, rebind on reconnect. */
  #controller = null;
  /** @type {((confirmed: boolean) => void) | null} The pending `openAndAwait()` caller. */
  #pendingResolve = null;
  /** @type {Promise<boolean> | null} Handed back by a re-entrant `openAndAwait()` call. */
  #pendingPromise = null;
  /** @type {'metric'|'imperial'} Which field set is currently VISIBLE. Display only. */
  #unit = 'metric';
  /**
   * The unit the customer last TYPED each measurement in — the authoritative
   * set (Addendum 4). Everything that decides a stored value reads these,
   * never `#unit`: the other set's display is clamped at the range extremes
   * (Addendum 3), so reading it back would convert 220 cm into a stored
   * 218 cm. Only `#handleMeasurementInput` moves them, and only the one whose
   * box was typed in; a unit toggle never moves either.
   *
   * Two flags, not one. A single panel-wide flag reached the very drift
   * Addendum 4 exists to prevent by a second route: a keystroke in the lb box
   * made imperial authoritative for the HEIGHT too, so a height typed as
   * 220 cm was re-read from its clamped 7 ft 2 in display and stored as
   * 218 cm. The customer edited their weight and their height moved, silently,
   * on a garment that is cut to those numbers and cannot be returned.
   * @type {'metric'|'imperial'}
   */
  #authoritativeHeightUnit = 'metric';
  /** @type {'metric'|'imperial'} The weight's half of the pair above. */
  #authoritativeWeightUnit = 'metric';
  /**
   * Which measurements the customer has typed over on this carried-mode panel.
   *
   * Per measurement, because `data-state` is the customer's only confirmation
   * that an override took, and marking both fields `own` the moment either one
   * is touched confirms something untrue: a line carrying the recipient's
   * height beside the buyer's prefilled weight would be styled as if both had
   * been supplied for this piece. Addendum 1's "half a body is not a body"
   * cannot be caught by the atomicity rule here, because a prefilled value is
   * valid by construction — so the display is what has to stop claiming it.
   *
   * The LINE is still atomic: `lineProperties()` carries both values together
   * whenever any of them is an override. This set only decides what each field
   * says about itself.
   * @type {Set<'height'|'weight'>}
   */
  #overriddenFields = new Set();
  /**
   * The five measurement boxes as they read when this panel was last showing
   * the PROFILE and nothing else — the state one line's overrides are undone
   * back to when the dialog closes (`#clearLineDecision`).
   *
   * Captured rather than re-derived. The stored profile is a pair of display
   * strings ("172 cm", "65 kg (143 lb)"); parsing them back into five boxes
   * would be a third place that knows the format, and it would have to decide
   * the imperial halves itself — which is `#mirrorValues()`'s job and is
   * CLAMPED (Addendum 3). Reading what the panel already shows sidesteps both.
   *
   * `input.defaultValue` looks like a free version of this and is not one: a
   * panel that FLIPPED from `first` (Addendum 7) was rendered by the `first`
   * branch, whose boxes are empty, so its `defaultValue`s are `''` and
   * restoring from them would blank a profile the customer can see.
   *
   * A `FitFields`, so the two authoritative units (Addendum 4) are part of the
   * snapshot rather than something the restore has to guess at.
   * @type {import('@theme/fit-profile-units').FitFields | null}
   */
  #lineSnapshot = null;
  /** @type {(() => void) | null} */
  #unsubscribe = null;
  /**
   * The `<dialog>` THIS element opened to show its panel, so it can close the
   * one it opened and never one that was already open around it.
   * @type {HTMLDialogElement | null}
   */
  #openedDialog = null;
  /**
   * Whether this panel is currently open to EDIT the shared cart profile
   * rather than to gate one add.
   *
   * The two look identical on screen and differ in exactly one place: what the
   * Save button writes. A gated add in `carried` mode deliberately leaves the
   * shared profile alone (see `#handleSaveClick`) — its numbers belong to the
   * one piece being added. An edit is the opposite: the numbers ARE the shared
   * profile, and every line that inherited the old ones has to be corrected.
   * Only `openForProfileEdit()` sets this, and it always clears it.
   * @type {boolean}
   */
  #editingProfile = false;

  connectedCallback() {
    this.#queryRefs();
    this.#unit = fitProfile.unitSystem;
    // Before anything reads `this.mode`. Liquid painted the mode from the cart
    // as it stood when the page was BUILT, and this element can connect long
    // afterwards: a panel morphed into the quick-add dialog, a page restored
    // from the back/forward cache, a cached page whose cart has since been
    // given a profile. Same reasoning as `renderFitGates()` running once at
    // startup — the server's answer is right on the first request and has no
    // way to stay right on its own.
    this.#syncModeToProfile();
    // Nothing has been typed yet, so Liquid's prefill is the authority — and
    // which of Liquid's two sets is CANONICAL depends on the mode.
    //
    // `carried`: metric. The cart attribute is stored as "220 cm" (or
    // "220 cm (7 ft 2 in)"), so the cm/kg boxes hold the number the profile
    // actually is, while the ft/in/lb boxes may hold a derived, CLAMPED
    // mirror of it. Starting imperial-authoritative for a US customer would
    // read that mirror back and store 218 cm for a 220 cm profile before a
    // single key was pressed. An untouched carried field is always valid, so
    // this cannot strand an error message in a hidden set.
    //
    // `first`: whichever set is on screen, because both are empty and the
    // error for a field the customer never filled in has to appear next to
    // the box they are looking at.
    const initial = this.mode === 'carried' ? 'metric' : this.#unit;
    this.#authoritativeHeightUnit = initial;
    this.#authoritativeWeightUnit = initial;
    this.#bindEvents();
    // Initial paint only — Liquid already painted both sets correctly, so
    // this establishes which one is visible without touching any value.
    this.#applyUnit(this.#unit);
    this.#renderConvertedHint();
    // Last, once the values and both authoritative units have settled. A panel
    // the server rendered `carried` never flips, so this is its ONLY chance to
    // record what the profile looks like in its own boxes — and it is the
    // panel the reported defect went through.
    this.#captureLineSnapshot();
    this.#unsubscribe = fitProfile.subscribe(this.#handleProfileChange);
  }

  /**
   * Records the boxes as the state this panel returns to between lines, or
   * forgets them when there is no profile behind the panel to return to.
   *
   * Called only where the panel is profile-backed AND nothing on it is an
   * override yet, which is what makes "what the boxes hold" and "what the
   * profile is" the same sentence: on connect, at the flip to `carried`, and
   * after a save that wrote the shared profile. Calling it anywhere else — in
   * particular after the customer has typed — would record one line's override
   * as the thing every later line gets reset to, which is the defect rather
   * than the fix.
   *
   * `first` mode deliberately snapshots nothing. Its boxes are empty or
   * half-typed and there is no profile behind them, so the honest reset is to
   * leave whatever the customer has put there alone.
   */
  #captureLineSnapshot() {
    this.#lineSnapshot = this.mode === 'carried' ? this.#fields() : null;
  }

  /**
   * The single subscription, covering both things the shared profile can
   * change under this panel: which unit set is on screen, and whether a
   * profile exists at all.
   *
   * One callback rather than two subscriptions because the two are ordered.
   * The mode decides what the panel IS — whether its fields are inherited
   * values or an empty form — and `#handleUnitSystemChange` mirrors and
   * repaints those fields, so it has to run against the settled mode.
   */
  #handleProfileChange = () => {
    this.#syncModeToProfile();
    this.#handleUnitSystemChange(fitProfile.unitSystem);
  };

  /**
   * Re-derives the mode from the profile — the whole of Addendum 7 in one
   * method.
   *
   * `data-mode` used to be decided once, in Liquid, from the cart's attributes
   * at render time. A customer who saved measurements for the first piece and
   * stayed on the page then added the second size through a panel still
   * painted `first`: the profile existed, so `decideSubmitAction` returned
   * `pass`, and the piece was added silently with the buyer's own
   * measurements — no panel, no gift checkbox, nothing on screen offering a
   * way to say "this one is for someone else" on a garment that is cut to
   * those numbers and cannot be returned.
   *
   * Reached from `fitProfile.subscribe`, never from the save handler, so
   * EVERY `<fit-profile>` on the page catches up no matter which one saved.
   * That is what makes this one method cover the shared quick-add panel,
   * which goes stale identically on a collection page whose single-variant
   * cards add straight from the grid.
   *
   * WHEN it runs is load-bearing. `#handleSaveClick` decides whether to write
   * the SHARED profile by reading `this.mode === 'first' || #editingProfile`,
   * and a panel that has flipped to `carried` must be on the non-writing side
   * of that branch — otherwise a later save on it would retarget every
   * inherited line at whoever this one line belongs to, the mis-cut-garment
   * failure `_fit_source` exists to prevent. The flip is safely after it: the
   * emit that gets here happens inside `fitProfile.save()`, which the branch
   * has already been entered to call.
   *
   * Returning early when the mode has not moved is not an optimisation. It is
   * what keeps a profile EDIT intact: `openForProfileEdit()` reveals the save
   * button and relabels it for the duration, and the save it performs emits
   * here — on a panel that is already `carried`, so nothing is repainted
   * underneath it.
   */
  #syncModeToProfile() {
    const next = fitProfile.isSet() ? 'carried' : 'first';
    if (next === this.mode) return;
    if (next === 'carried') this.#adoptProfileValues();
    this.#applyMode(next);
    // The flip is the moment a panel becomes profile-backed, and its boxes now
    // hold either what was just saved or what `#adoptProfileValues()` just
    // copied in — the profile, either way, with nothing overridden on top of
    // it yet. A flip in the other direction (the cart emptied) leaves the
    // panel with no profile to return to, which `#captureLineSnapshot` reads
    // off the mode.
    this.#captureLineSnapshot();
    this.#renderConvertedHint();
  }

  /**
   * Shows one mode's furniture and hides the other's.
   *
   * `[data-mode-only]` is the only marker, so this needs no list of elements
   * kept in step with `fit-profile-panel.liquid` — a new piece of mode
   * furniture is covered the moment it is rendered with the attribute. What
   * cannot be expressed that way is here instead: the save button, which is
   * one element with a label per mode, and the summary sentence, which has to
   * be built rather than revealed.
   *
   * The button's LABEL is the mode's business and is set here. Its VISIBILITY
   * is not: two things move it and they must not disagree, so it has a rule of
   * its own in `#renderSaveButton`.
   *
   * @param {'first'|'carried'} mode
   */
  #applyMode(mode) {
    this.dataset.mode = mode;

    for (const element of this.querySelectorAll('[data-mode-only]')) {
      if (element instanceof HTMLElement) element.hidden = element.dataset.modeOnly !== mode;
    }

    const button = this.saveButton;
    if (button) {
      const label = mode === 'carried' ? button.dataset.carriedLabel : button.dataset.firstLabel;
      if (label) button.textContent = label;
    }
    this.#renderSaveButton();

    this.#renderSummary();
    this.#renderFieldStates();
  }

  /**
   * Whether the panel's own button is on screen. One rule, in one place,
   * because two things move it and they used to disagree.
   *
   * At rest in `carried` mode it is hidden, for the reason Liquid hides it on a
   * carried load: it is a dead control there, a full-width primary "Add to
   * cart" stacked above the real one, outside the product form and doing
   * nothing when pressed. The approved canvas draws a single add control, and
   * it is the native one.
   *
   * While the panel is OPEN awaiting a decision it is the only control there
   * is, in either mode. Since Addendum 8 a `carried` panel can be gated too: a
   * single-variant grid card opens the shared panel in its own `<dialog>`, and
   * the native button that opened it is behind a modal backdrop and suppressed
   * by `gatingForms` besides. Hidden, that dialog would show the customer their
   * measurements, a carry-over sentence and a gift checkbox, and no way to say
   * yes — the add could only ever be abandoned. Pressing it is the "just press
   * the button" path, on which `lineProperties()` reads `inherited`.
   *
   * Keyed on `#pendingPromise` rather than a flag of its own, so the button is
   * visible over exactly the interval the panel is awaiting an answer — set by
   * `openAndAwait()`, cleared by `#resolve()`, whichever way the gate ends.
   * That is also what stops a mode flip mid-gate from hiding it: the customer
   * saves in `first` mode, `fitProfile.save()` emits, `#applyMode('carried')`
   * runs from inside the still-open gate, and the button has to survive until
   * `#resolve()` — which then hides it, because by then the mode really is
   * `carried` and the panel really is at rest.
   */
  #renderSaveButton() {
    const button = this.saveButton;
    if (!button) return;
    button.hidden = this.mode === 'carried' && !this.#pendingPromise;
  }

  /**
   * Writes the "Fitting to 165 cm and 52 kg" line a `carried` panel leads
   * with.
   *
   * Liquid renders it for a cold load and leaves the element empty and hidden
   * in `first` mode, so a flip has to both fill it in and reveal it — an
   * un-hidden empty sentence is a checkmark ticking nothing, which is what the
   * summary snippet's own `[hidden]` guard exists to prevent.
   *
   * The format is `fittingToSentence` in the pure module, where `node --test`
   * can reach it and where `scripts/test_fit_profile_sentence_agrees.py`
   * compares it against the Liquid. The PREFIX is merchant copy and is not
   * duplicated: it arrives on `data-fitting-prefix`, written by the same
   * Liquid that builds the server-side copy of this sentence.
   */
  #renderSummary() {
    const target = this.summaryText;
    if (!target) return;
    const sentence = fittingToSentence(this.carriedIntro?.dataset.fittingPrefix ?? '', fitProfile.get());
    target.textContent = sentence;
    if (this.summarySentence) this.summarySentence.hidden = sentence === '';
  }

  /**
   * Fills this panel's empty measurement boxes from the profile, so a flipped
   * panel reads like a reloaded one.
   *
   * The panel that just saved needs none of this — the numbers the customer
   * typed are still in its boxes, which is exactly why the two modes share one
   * set of fields. Every OTHER panel on the page flips with empty boxes, and
   * an empty box labelled `inherited` is a lie about what the line will carry:
   * `lineProperties()` would still stamp the profile's values, so the cart
   * would be right while the screen said nothing was there.
   *
   * A box the customer HAS typed in is never touched, and the check is made
   * against that measurement's authoritative set rather than the visible one
   * (Addendum 4) — a half-finished entry is still the customer's, and
   * overwriting it with the profile would take back a number they were in the
   * middle of changing.
   *
   * Adopting the profile makes metric authoritative for that measurement, for
   * the reason `connectedCallback` starts a `carried` panel metric: the cart
   * attribute IS the canonical metric value, while the imperial boxes about to
   * be filled from it are a CLAMPED mirror (Addendum 3). `#mirrorValues()`
   * then paints the other set, exactly as Liquid's bootstrap paints both sets
   * on a carried load.
   */
  #adoptProfileValues() {
    const { height, weight } = fitProfile.get();
    if (this.heightCm && !this.#hasTypedMeasurement('height')) {
      this.heightCm.value = measurementNumber(height);
      this.#authoritativeHeightUnit = 'metric';
    }
    if (this.weightKg && !this.#hasTypedMeasurement('weight')) {
      this.weightKg.value = measurementNumber(weight);
      this.#authoritativeWeightUnit = 'metric';
    }
    this.#mirrorValues();
  }

  /**
   * Whether the customer has put anything in this measurement's authoritative
   * boxes. Not "is it valid" — a partial entry counts, because it is theirs.
   * @param {'height'|'weight'} measurement
   * @returns {boolean}
   */
  #hasTypedMeasurement(measurement) {
    if (measurement === 'height') {
      return this.#authoritativeHeightUnit === 'imperial'
        ? Boolean(this.heightFeet?.value || this.heightInches?.value)
        : Boolean(this.heightCm?.value);
    }
    return this.#authoritativeWeightUnit === 'imperial'
      ? Boolean(this.weightLb?.value)
      : Boolean(this.weightKg?.value);
  }

  /**
   * Fires on every `fitProfile.unitSystem` change (any instance's toggle).
   * Re-renders the non-authoritative set from the authoritative one before
   * switching visibility, so whichever set the customer is about to look at
   * shows their own measurement.
   *
   * A toggle never changes which set is authoritative, so this is purely
   * cosmetic — the customer can toggle back and forth all day and the value
   * a save would store does not move.
   * @param {'metric'|'imperial'} system
   */
  #handleUnitSystemChange(system) {
    if (system !== this.#unit) this.#mirrorValues();
    this.#applyUnit(system);
    this.#renderConvertedHint();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    // Torn down (e.g. a section re-render) while something awaited a decision.
    this.#resolve(false);
  }

  /** @returns {'first'|'carried'} */
  get mode() {
    return this.dataset.mode === 'carried' ? 'carried' : 'first';
  }

  /**
   * Which bucket this piece's cart line falls into. `to_confirm` wins over
   * everything, even a field edited before the gift box was ticked. `own`
   * only once the customer has edited a field on this panel instance
   * (`#overriddenFields`, the same set that drives the `own` `data-state`).
   * `inherited` otherwise — including `first` mode, where the values just
   * saved to the line are simultaneously the cart profile itself, so a later
   * profile edit should propagate to it like any other inherited line. Safe
   * to read before `connectedCallback` has run: `giftUnknown` is undefined
   * pre-init, so this falls through to `inherited`.
   *
   * Used by `lineProperties()` below, which is what the add-to-cart flow
   * should actually call.
   * @returns {'to_confirm'|'own'|'inherited'}
   */
  get lineSource() {
    // ANY overridden field makes the whole line an override: the values travel
    // together or not at all (Addendum 1). Only the per-field `data-state`
    // display distinguishes which box the customer actually typed in — see
    // `fieldStateFor` beside this rule in fit-profile-units.js, where both are
    // reachable from `node --test`.
    return lineSourceFor({ gift: Boolean(this.giftUnknown?.checked), overridden: this.#overriddenFields });
  }

  /**
   * The complete property set for THIS piece's line — the single way a line
   * should be assembled. Task 6 calls this, never `fitProfile.properties(...)`
   * directly: an override belongs to the element that owns the fields, the
   * shared value belongs to `fitProfile`, and building a line from
   * `fitProfile` alone cannot say "this line differs from the profile"
   * without mutating the profile for every future line too.
   *
   * `own` re-validates the authoritative fields itself rather than trusting
   * an earlier save click; if they are currently invalid, falls back to the
   * cart profile rather than ever returning an unvalidated number. It reads
   * the unit the customer typed in, never the one on screen — see
   * `#authoritativeHeightUnit` / `#authoritativeWeightUnit` and Addendum 4.
   * @returns {{Height: string, Weight: string, _fit_source: string}}
   */
  lineProperties() {
    const source = this.lineSource;
    if (source !== 'own') return fitProfile.properties(source);
    const result = this.#validate();
    if (!result.ok) return fitProfile.properties('inherited');
    return { Height: result.height, Weight: result.weight, _fit_source: 'own' };
  }

  /**
   * Opens the panel (a no-op if already visible, or already awaited — the
   * same promise is handed back rather than orphaning the first caller),
   * opens the `<dialog>` it lives in if it lives in one, focuses the active
   * unit set's first field, and resolves once the customer saves a profile,
   * confirms the gift box, dismisses the dialog, or the element is torn down.
   * @returns {Promise<boolean>}
   */
  openAndAwait() {
    if (this.#pendingPromise) return this.#pendingPromise;
    if (this.panel) this.panel.hidden = false;
    this.#pendingPromise = new Promise((resolve) => {
      this.#pendingResolve = resolve;
    });
    // After `#pendingPromise` is set, which is what the rule reads: for as long
    // as this panel is awaiting a decision its own button is the only control
    // that can settle it, in `carried` mode as much as in `first`.
    this.#renderSaveButton();
    this.#showHostDialog();
    const first = this.#unit === 'imperial' ? this.heightFeet : this.heightCm;
    first?.focus();
    return this.#pendingPromise;
  }

  /**
   * Opens this panel to change the shared cart profile — the cart's "Change
   * your measurements" link.
   *
   * Same panel, same fields, same Save button as a gated add; the flag is what
   * redirects Save to `fitProfile.save()`. The gift box is cleared on the way
   * in because it is a statement about one piece ("this one is a gift and I do
   * not know their measurements") and says nothing about the buyer's own body,
   * which is what is being edited here.
   *
   * @returns {Promise<boolean>} Whether the customer settled it (saved), rather
   *   than dismissing the panel.
   */
  async openForProfileEdit() {
    if (this.giftUnknown?.checked) {
      this.giftUnknown.checked = false;
      this.#handleGiftChange();
    }
    // Hidden rather than merely cleared. Left on screen it reads as a question
    // about the body being edited, and ticking it would make Save do nothing
    // at all (see `#handleSaveClick`) — a dead button with no explanation.
    const giftRow = this.giftUnknown?.closest('.fit-profile-gift');
    if (giftRow instanceof HTMLElement) giftRow.hidden = true;

    // The button says "Add to cart" in `carried` mode, and on this path it adds
    // nothing — it saves the shared profile. The replacement comes from the
    // copy layer (`data-edit-label`, rendered by fit-profile-panel.liquid from
    // the block's `save_button_text_edit` setting), never from a string here,
    // so the merchant can edit it and the copy-agreement test can see it.
    //
    // REVEALING the button is not done here any more. It was, back when this
    // was the only path on which a `carried` panel's button had a job; since
    // Addendum 8 a gated `carried` panel has one too, so the rule belongs with
    // the interval it depends on rather than duplicated per caller. This method
    // opens the panel through `openAndAwait()` like any other caller, and
    // `#renderSaveButton` shows the button for exactly as long as it is open.
    // Only the LABEL is this path's business, and only the label is restored
    // below.
    const button = this.saveButton;
    const editLabel = button?.dataset.editLabel;
    const restoreLabel = button && editLabel ? button.textContent : null;
    if (button && editLabel) button.textContent = editLabel;

    this.#editingProfile = true;
    try {
      return await this.openAndAwait();
    } finally {
      this.#editingProfile = false;
      if (giftRow instanceof HTMLElement) giftRow.hidden = false;
      if (button && restoreLabel !== null) button.textContent = restoreLabel;
    }
  }

  /**
   * Makes this panel actually visible when it lives inside a `<dialog>`.
   *
   * The shared quick-add panel sits in one (see `quick-add-modal.liquid`),
   * because a card's "Add" button adds straight from the grid without opening
   * the quick-add modal. A closed `<dialog>` is `display: none`, so unhiding
   * the panel inside it changes nothing the customer can see: the click would
   * appear to do nothing at all and the piece would never be added. Opening
   * the dialog is what turns the gate from a dead end into a question.
   *
   * The `close` listener is the other half. Escape and the dismiss button both
   * close the dialog without a decision; without settling the promise the
   * interceptor would await forever and `gatingForms` would suppress every
   * later click on that form — the same dead end from the other direction.
   *
   * This runs at most once per gate, and only because `openAndAwait()` returns
   * early when `#pendingPromise` is already set. That short-circuit is
   * load-bearing here, not just an optimisation: the shared panel is reached by
   * every panel-less form on the page, so without it each extra caller would
   * add another `once: true` close listener, and a single dismissal would fire
   * a pile of them. Keep the two together.
   */
  #showHostDialog() {
    const dialog = this.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch {
        // Already open elsewhere in the tree, or not yet connected. A
        // non-modal open still puts the panel on screen, which is the part
        // that matters.
        if (!dialog.open) dialog.show();
      }
      if (dialog.open) this.#openedDialog = dialog;
    }

    dialog.addEventListener(
      'close',
      () => {
        this.#resolve(false);
        this.#clearLineDecision();
      },
      { once: true }
    );
  }

  /**
   * Forgets the per-line decision this panel was holding, once it is off
   * screen.
   *
   * Only a panel in a dialog gets here, which in practice means the shared
   * quick-add panel — the one element that serves many different lines. A
   * product page's inline panel keeps its overrides on purpose: it belongs to
   * one visible form, the customer can read what it says, and no other line is
   * ever added through it.
   *
   * The gift box was the first thing forgotten here. "This one is a gift and I
   * do not know their measurements" is a statement about a single piece, and
   * it is ticked where the customer can see it; left set, it would keep
   * stamping the "ask me" sentinel onto every later quick-add from behind a
   * closed dialog, with nothing on screen saying so.
   *
   * The typed numbers are exactly as much a statement about a single piece,
   * and they are the half that gets cut into cloth — so the same argument
   * reaches them, and `clearedLineDecision` in the pure module is where it is
   * written down and tested. It became reachable with Addendum 8: the shared
   * panel now opens in `carried` mode once per single-variant grid add, so a
   * customer who typed a recipient's measurements for one dress and then added
   * a second WITHOUT touching the panel got the second line stamped `own` on
   * the recipient's body. `own` is precisely what `reconcileCartLines()`
   * refuses to touch, so nothing downstream could ever correct it, and the
   * summary sentence above the boxes went on reading the buyer's own numbers
   * while the boxes underneath it said otherwise.
   *
   * Both authoritative units go back too, not just the values. A weight typed
   * in lb for the gift leaves this panel weight-authoritative-imperial; a next
   * line holding the restored metric numbers under an imperial authority would
   * read the weight back out of a clamped lb display, which is the silent
   * drift Addendum 4 exists to prevent. They ride along inside the snapshot,
   * so there is no way to restore one without the other.
   *
   * Runs on the dialog's `close` event, which is queued as a task. The
   * interceptor's continuation is a microtask, so it has already read
   * `lineProperties()` off this panel by the time this runs — the line that
   * was just added keeps the override the customer typed for it, and only the
   * NEXT one starts fresh. Moving this any earlier takes the override off the
   * very line it was typed for.
   *
   * With no snapshot there is no profile to return to (`first` mode, never
   * flipped), and the boxes are left exactly as they are: a half-typed entry
   * is the customer's, nothing reads it until it is saved, and blanking it
   * would lose a measurement they were part-way through.
   */
  #clearLineDecision() {
    const { fields, overridden, gift } = clearedLineDecision(this.#lineSnapshot);

    if (this.giftUnknown && this.giftUnknown.checked !== gift) {
      this.giftUnknown.checked = gift;
      this.#renderFieldVisibility();
    }

    this.#overriddenFields = overridden;

    if (fields) {
      this.#authoritativeHeightUnit = fields.heightUnit ?? 'metric';
      this.#authoritativeWeightUnit = fields.weightUnit ?? 'metric';
      if (this.heightCm) this.heightCm.value = fields.cm;
      if (this.weightKg) this.weightKg.value = fields.kg;
      if (this.heightFeet) this.heightFeet.value = fields.feet;
      if (this.heightInches) this.heightInches.value = fields.inches;
      if (this.weightLb) this.weightLb.value = fields.lb;
    }

    // A message about a number that is no longer in the box is a lie about the
    // next line, so the errors go with the values that earned them.
    this.#clearAllErrors();
    this.#renderFieldStates();
    this.#renderConvertedHint();
  }

  /** @param {boolean} confirmed */
  #resolve(confirmed) {
    const resolve = this.#pendingResolve;
    this.#pendingResolve = null;
    this.#pendingPromise = null;
    // The panel is at rest again, so the button goes back to whatever the
    // CURRENT mode says — not to whatever it was when the gate opened. A
    // `first`-mode gate that saved has flipped to `carried` in between, and
    // restoring the captured value would leave a dead primary "Add to cart"
    // above the real one for the rest of the page's life.
    this.#renderSaveButton();

    // Close before resolving, not after: `close` fires as a task and the
    // awaiting continuation runs as a microtask, so the interceptor reads
    // `lineProperties()` off this panel before anything can react to the
    // dialog closing. Only ever the dialog this element opened.
    const dialog = this.#openedDialog;
    this.#openedDialog = null;
    if (dialog?.open) dialog.close();

    if (resolve) resolve(confirmed);
  }

  #queryRefs() {
    this.panel = refIn(this, 'panel');
    this.unitToggle = refIn(this, 'unitToggle');
    this.metricFields = refIn(this, 'metricFields');
    this.imperialFields = refIn(this, 'imperialFields');
    this.heightCm = refIn(this, 'heightCm');
    this.weightKg = refIn(this, 'weightKg');
    this.heightFeet = refIn(this, 'heightFeet');
    this.heightInches = refIn(this, 'heightInches');
    this.weightLb = refIn(this, 'weightLb');
    this.giftUnknown = refIn(this, 'giftUnknown');
    this.giftConsequence = refIn(this, 'giftConsequence');
    this.saveButton = refIn(this, 'saveButton');
    this.convertedHint = refIn(this, 'convertedHint');
    // The carried intro carries the sentence's merchant prefix; the sentence
    // span is what has to be un-hidden when a flip gives it something to say.
    this.carriedIntro = refIn(this, 'carriedIntro');
    this.summarySentence = refIn(this, 'summarySentence');
    this.summaryText = refIn(this, 'summaryText');

    // heightError/weightError each render twice (metric set + imperial set),
    // so they must be scoped to their container, not a single element-wide query.
    this.heightErrorMetric = this.metricFields && refIn(this.metricFields, 'heightError');
    this.weightErrorMetric = this.metricFields && refIn(this.metricFields, 'weightError');
    this.heightErrorImperial = this.imperialFields && refIn(this.imperialFields, 'heightError');
    this.weightErrorImperial = this.imperialFields && refIn(this.imperialFields, 'weightError');
  }

  /** @returns {HTMLInputElement[]} Every measurement input that exists in this instance. */
  #measurementInputs() {
    return [this.heightCm, this.weightKg, this.heightFeet, this.heightInches, this.weightLb].filter(
      (input) => input != null
    );
  }

  #bindEvents() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.unitToggle?.addEventListener('click', this.#handleUnitToggleClick, { signal });
    this.giftUnknown?.addEventListener('change', this.#handleGiftChange, { signal });
    this.saveButton?.addEventListener('click', this.#handleSaveClick, { signal });
    for (const input of this.#measurementInputs()) {
      input.addEventListener('input', this.#handleMeasurementInput, { signal });
    }
  }

  /**
   * Shows the active unit's field set, hides the other, and updates the
   * toggle buttons' pressed state. Never touches field values — Liquid
   * already painted both sets correctly on first load; this only decides
   * which one is visible.
   *
   * @param {'metric'|'imperial'} system
   */
  #applyUnit(system) {
    this.#unit = system;
    if (this.unitToggle) {
      for (const button of this.unitToggle.querySelectorAll('[data-unit]')) {
        button.setAttribute('aria-pressed', String(button.dataset.unit === system));
      }
    }
    this.#renderFieldVisibility();
  }

  /** Reconciles field-set visibility from both the active unit and the gift checkbox. */
  #renderFieldVisibility() {
    const giftOn = Boolean(this.giftUnknown?.checked);
    if (this.metricFields) this.metricFields.hidden = giftOn || this.#unit !== 'metric';
    if (this.imperialFields) this.imperialFields.hidden = giftOn || this.#unit !== 'imperial';
    if (this.unitToggle) this.unitToggle.hidden = giftOn;
    if (this.giftConsequence) this.giftConsequence.hidden = !giftOn;
  }

  /**
   * This panel's raw field values, tagged with the authoritative unit — the
   * single hand-off point between the DOM and the pure module. Every decision
   * about what gets stored is made over this object by `normalizeFitFields`,
   * which is where the "never read the visible set" rule is enforced and
   * tested.
   * @returns {import('@theme/fit-profile-units').FitFields}
   */
  #fields() {
    return {
      heightUnit: this.#authoritativeHeightUnit,
      weightUnit: this.#authoritativeWeightUnit,
      cm: this.heightCm?.value ?? '',
      kg: this.weightKg?.value ?? '',
      feet: this.heightFeet?.value ?? '',
      inches: this.heightInches?.value ?? '',
      lb: this.weightLb?.value ?? '',
    };
  }

  /**
   * Re-renders the non-authoritative set from the authoritative one, so a
   * customer toggling units sees their measurement in both.
   *
   * Display only. `mirrorFitFields` returns the authoritative members
   * unchanged, so this writes nothing the customer typed, and running it
   * repeatedly (toggle away, toggle back) cannot drift the stored value —
   * which is precisely what reading the visible set used to do at the four
   * clamped extremes (Addendum 4).
   */
  #mirrorValues() {
    const mirrored = mirrorFitFields(this.#fields());
    // Each measurement is written into whichever set is NOT its own authority.
    // Split authority is an ordinary state here, not a corner case: one
    // keystroke in the other unit's box produces it.
    if (this.#authoritativeHeightUnit === 'metric') {
      if (this.heightFeet) this.heightFeet.value = mirrored.feet;
      if (this.heightInches) this.heightInches.value = mirrored.inches;
    } else if (this.heightCm) {
      this.heightCm.value = mirrored.cm;
    }
    if (this.#authoritativeWeightUnit === 'metric') {
      if (this.weightLb) this.weightLb.value = mirrored.lb;
    } else if (this.weightKg) {
      this.weightKg.value = mirrored.kg;
    }
  }

  #handleUnitToggleClick = (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-unit]') : null;
    if (!button) return;
    const system = button.getAttribute('data-unit') === 'imperial' ? 'imperial' : 'metric';
    if (system === this.#unit) return;
    // Persisted and broadcast through the singleton so every `<fit-profile>`
    // instance on the page (e.g. a product page panel and a quick-add modal
    // panel) stays in sync with one toggle click — each instance mirrors its
    // own values in its `fitProfile.subscribe` callback (see
    // `#handleUnitSystemChange`), not here.
    fitProfile.unitSystem = system;
  };

  #handleGiftChange = () => {
    this.#renderFieldVisibility();
    this.#clearAllErrors();
    this.#renderFieldStates();
  };

  /**
   * Which measurement an input belongs to — the mapping that makes both
   * "authority" and "override" per-measurement rather than per-panel.
   * @param {HTMLInputElement} input
   * @returns {'height'|'weight'|null}
   */
  #measurementOf(input) {
    if (input === this.heightCm || input === this.heightFeet || input === this.heightInches) return 'height';
    if (input === this.weightKg || input === this.weightLb) return 'weight';
    return null;
  }

  /**
   * Which unit set an input belongs to. Read off the input's identity rather
   * than off `#unit`, because "which set is visible" is exactly the thing
   * Addendum 4 says must never decide a stored value — and the two can differ
   * for one measurement while agreeing for the other.
   * @param {HTMLInputElement} input
   * @returns {'metric'|'imperial'}
   */
  #unitOf(input) {
    return input === this.heightCm || input === this.weightKg ? 'metric' : 'imperial';
  }

  /**
   * The `data-state` one field should be showing, or `null` in a mode that
   * does not label fields at all (`first`, where nothing is inherited).
   * @param {HTMLInputElement} input
   * @returns {'own'|'inherited'|'off'|null}
   */
  #stateForInput(input) {
    const measurement = this.#measurementOf(input);
    if (!measurement) return null;
    return fieldStateFor(measurement, {
      gift: Boolean(this.giftUnknown?.checked),
      mode: this.mode,
      overridden: this.#overriddenFields,
    });
  }

  /** Repaints every field's `data-state` from the current override set. */
  #renderFieldStates() {
    for (const input of this.#measurementInputs()) {
      const state = this.#stateForInput(input);
      if (state) input.dataset.state = state;
      else delete input.dataset.state;
    }
  }

  /**
   * Editing a measurement field makes THAT measurement's set authoritative and
   * marks THAT measurement as the customer's own. The line still carries both
   * values together — see `lineSource` — but a field the customer never
   * touched keeps saying `inherited`, because that is what it is.
   * @param {Event} event
   */
  #handleMeasurementInput = (event) => {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;
    const measurement = this.#measurementOf(input);
    if (!measurement) return;

    // The set being typed into becomes the authority for this measurement
    // alone (Addendum 4). Runs in every mode and on every keystroke.
    if (measurement === 'height') this.#authoritativeHeightUnit = this.#unitOf(input);
    else this.#authoritativeWeightUnit = this.#unitOf(input);

    if (this.mode === 'carried') this.#overriddenFields.add(measurement);
    this.#renderFieldStates();
    this.#renderConvertedHint();
  };

  /**
   * @param {'height'|'weight'} field
   * @param {string} message
   * @param {'metric'|'imperial'} [unit] Which field set to put the message in.
   *   Defaults to the visible one, which is right for a message about the panel
   *   as a whole (a failed save). A validation message belongs beside the
   *   number it describes, so callers pass that measurement's AUTHORITATIVE
   *   unit — which, since authority is per measurement, is not necessarily the
   *   same set for height and weight.
   */
  #showError(field, message, unit = this.#unit) {
    const target =
      unit === 'imperial'
        ? field === 'height'
          ? this.heightErrorImperial
          : this.weightErrorImperial
        : field === 'height'
          ? this.heightErrorMetric
          : this.weightErrorMetric;
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }

  /**
   * Rewrites the "Recorded for the workroom as 172 cm and 65 kg" line under the
   * imperial fields. The rule itself is `convertedHintFor` in
   * fit-profile-units.js, where it is reachable from `node --test`; this is
   * the DOM half.
   *
   * Called on every keystroke and every unit change, because both can move the
   * validated value. Until this branch the element was rendered and never
   * queried at all.
   *
   * The prefix comes off `data-recorded-prefix`, rendered by
   * fit-profile-panel.liquid from the block's `recorded_prefix` setting, for
   * the same reason the edit label does: it is merchant copy, the merchant can
   * change it, and scripts/test_fit_profile_copy_agree.py can see it there.
   */
  #renderConvertedHint() {
    const hint = this.convertedHint;
    if (!hint) return;

    const { text, hidden } = convertedHintFor(hint.dataset.recordedPrefix ?? '', this.#fields());
    hint.textContent = text;
    hint.hidden = hidden;
  }

  #clearAllErrors() {
    for (const el of [this.heightErrorMetric, this.weightErrorMetric, this.heightErrorImperial, this.weightErrorImperial]) {
      if (!el) continue;
      el.textContent = '';
      el.hidden = true;
    }
  }

  /**
   * Validates the AUTHORITATIVE unit set's current field values — not the
   * visible one (Addendum 4). Pure with respect to the DOM — reads, never
   * writes — so both `#handleSaveClick` (which additionally shows errors)
   * and `lineProperties()` (which needs the validated strings, or none at
   * all) share one source of truth. Overrides are atomic: both fields must
   * validate before either counts, which `normalizeFitFields` enforces.
   * @returns {{ok: true, unit: string, height: string, weight: string}
   *   | {ok: false, unit: string, heightResult: object, weightResult: object}}
   */
  #validate() {
    return normalizeFitFields(this.#fields());
  }

  #handleSaveClick = async () => {
    this.#clearAllErrors();

    if (this.giftUnknown?.checked) {
      // The sentinel is built by `fitProfile.properties('to_confirm')` at
      // add-to-cart time — nothing to validate or save here. The shared
      // profile is deliberately left untouched: "I don't know their
      // measurements" says nothing about the buyer's own body.
      this.#resolve(true);
      return;
    }

    const result = this.#validate();
    if (!result.ok) {
      // The offending field lives in ITS measurement's authoritative set,
      // which is not necessarily the one on screen: the customer can type an
      // out-of-range number and then toggle units. Bring the offending set
      // into view, or the message ("between 100 cm and 220 cm") would be
      // pinned to a ft/in field it does not describe, next to a number the
      // customer never typed. Routed through the singleton so every panel on
      // the page moves together, exactly as a toggle click does.
      //
      // Each message is placed in its own measurement's set, not in whatever
      // ends up visible: with authority held per measurement, height and
      // weight can legitimately disagree about which set they were typed in,
      // and only one of the two can be brought into view. The first failing
      // one wins that, and the other message is still correct and still
      // attached to the number it describes when the customer toggles back.
      const offending = result.heightResult.ok ? result.weightUnit : result.heightUnit;
      if (offending !== this.#unit) fitProfile.unitSystem = offending;
      if (!result.heightResult.ok) this.#showError('height', result.heightResult.error, result.heightUnit);
      if (!result.weightResult.ok) this.#showError('weight', result.weightResult.error, result.weightUnit);
      return;
    }

    // Only `first` mode's save establishes the shared cart-level profile.
    // Writing `carried` mode's own values here would retarget every future
    // "inherited" line at whoever this line belongs to — the mis-cut-garment
    // failure `_fit_source` exists to prevent. `lineProperties()` puts this
    // panel's own values on its own line without touching the shared profile.
    //
    // A deliberate profile EDIT is the exception, and the only one: the
    // customer opened this panel from the cart to change the measurements every
    // inherited line took, so here the shared profile is precisely what they
    // mean to write. `openForProfileEdit()` is the single place that flag is
    // set, so there is no arrangement of clicks in which a gated add takes this
    // branch by accident.
    if (this.mode === 'first' || this.#editingProfile) {
      if (this.saveButton) this.saveButton.disabled = true;
      try {
        await fitProfile.save({ height: result.height, weight: result.weight });
      } catch {
        this.#showError('height', SAVE_FAILED_MESSAGE);
        this.#showError('weight', SAVE_FAILED_MESSAGE);
        return;
      } finally {
        if (this.saveButton) this.saveButton.disabled = false;
      }
      // What was typed IS the shared profile now, so nothing on this panel is
      // an override any more. Without this the shared quick-add panel would
      // keep reporting `own` for every later add, stamping a provenance the
      // customer never expressed onto lines that merely inherited — and
      // `own` is exactly what future reconciles refuse to touch.
      if (this.#editingProfile) this.#overriddenFields.clear();
      // Mirror BEFORE snapshotting, because a snapshot is restored as a whole
      // and has to be internally consistent. Only the authoritative set was
      // typed in; the other one still holds the mirror of the PREVIOUS
      // profile, and nothing repaints it until the customer toggles units.
      // Snapshotting it in that state and writing it back on the next dialog
      // close puts "170 cm" beside "5 ft 5 in" — 165 cm — on one panel.
      // Nothing wrong can be stored from it, since the authoritative half is
      // the half that is read, but the customer looking at the imperial boxes
      // is being shown someone else's height.
      this.#mirrorValues();
      // These boxes ARE the shared profile now, so they are what every later
      // line on this panel has to be returned to. Without this, the cart's
      // "Change your measurements" link would leave the shared panel holding a
      // snapshot of the profile it just replaced — and this element is exactly
      // the one that link opens (`findProfilePanel` prefers `data-fit-shared`),
      // so the very dialog close that ends the edit would put the old numbers
      // back on screen.
      this.#captureLineSnapshot();
    }

    this.#renderFieldStates();
    this.#renderConvertedHint();

    // The panel deliberately stays open (design spec, Addendum 7).
    //
    // `first` mode used to hide it here, on the reasoning that it had opened
    // only to collect this profile and its job was done. That was true of the
    // panel and false of the customer: the next thing they do is add a second
    // size of the same dress, and with the panel gone — and `data-mode` left
    // saying `first` — that add went through the gate as `pass`, silently, on
    // the buyer's own measurements.
    //
    // A successful save has already flipped this panel to `carried` by the
    // time it gets here (`#syncModeToProfile`, reached from inside
    // `fitProfile.save()`), and a `carried` panel is a permanent fixture: it
    // sits under the buy button, prefilled and grey, with the gift checkbox
    // beside it, which is the whole affordance the second piece needs.

    this.#resolve(true);
  };
}

if (!customElements.get('fit-profile')) {
  customElements.define('fit-profile', FitProfileElement);
}

// ---------------------------------------------------------------------------
// Gating Horizon's own product form
// ---------------------------------------------------------------------------

/**
 * The line-item property keys this file owns. Listed so a set that no longer
 * applies can be cleaned off a form rather than left behind on it.
 */
const FIT_PROPERTY_KEYS = ['Height', 'Weight', '_fit_source'];

/**
 * Forms whose gate has already been cleared for the submit currently in
 * flight. Without this, `requestSubmit()` below re-enters this same handler,
 * and any case where the gate stays unsatisfied afterwards spins forever:
 * the gift box, for instance, deliberately never writes a cart profile, so
 * `fitProfile.isSet()` is still false on the way back through.
 *
 * A WeakSet keyed on the form, not a dataset flag, because it must not
 * survive anything: it is added immediately before `requestSubmit()` and
 * consumed by the synchronous first statement of the re-entrant handler.
 * @type {WeakSet<HTMLFormElement>}
 */
const clearedForms = new WeakSet();

/**
 * Forms with a gate already open and awaiting the customer.
 *
 * `clearedForms` guards this file's own re-entrant `requestSubmit()`. It does
 * nothing about two INDEPENDENT clicks, which is a different problem:
 * `openAndAwait()` deliberately hands the same pending promise to a second
 * caller (see its doc), so one Save click resolves both continuations and each
 * one runs `requestSubmit()`. Two `/cart/add` calls, one line added twice, on a
 * cut-to-order non-returnable garment. Plausible on mobile, where a double tap
 * on "Add to cart" is a slip rather than an intention.
 *
 * Horizon's own double-submit guard cannot help: it engages only once
 * `handleSubmit` has run, and it never sees the first, intercepted attempt.
 *
 * So the second click is dropped where it arrives, before it can queue a
 * continuation at all. The first click's flow completes the add; the second
 * has nothing to contribute. A WeakSet so a form discarded by a section
 * re-render is collected rather than pinned.
 * @type {WeakSet<HTMLFormElement>}
 */
const gatingForms = new WeakSet();

/**
 * Panels currently holding an open gate, whichever form opened it.
 *
 * `gatingForms` is keyed on the form, which catches the same button pressed
 * twice. It cannot see the other race: on a collection page every card with no
 * panel of its own resolves to the SAME shared panel, and `openAndAwait()`
 * deliberately hands a second caller the pending promise rather than orphaning
 * the first. Click Add on card A, then card B while the dialog is open, then
 * Save once, and both continuations resume — two different products added from
 * one interaction, on a non-returnable cut-to-order storefront.
 *
 * Keyed on the panel, so it catches exactly the clicks `gatingForms` cannot: a
 * different form answering to a panel that is already asking a question. A
 * WeakSet so a panel discarded by a section re-render is collected.
 * @type {WeakSet<HTMLElement>}
 */
const gatingPanels = new WeakSet();

/**
 * The one `<fit-profile>` panel this form answers to — for BOTH the gate and
 * the properties. Resolved once per submit and passed to both, so there is no
 * arrangement of panels in which a customer types their height into one panel
 * and the line is stamped from another.
 *
 * Two candidates, in order (see `resolveFitPanel` for why this is one function
 * and not two lookups):
 *
 * 1. A panel inside the form's own scope — its section on a product page, or
 *    the dialog it lives in. It belongs to this line and can carry an override
 *    typed for this exact piece, so it wins.
 * 2. The shared panel, marked `data-fit-shared` and rendered once per page by
 *    `quick-add-modal.liquid`. Every quick-add card on a collection or
 *    recommendations grid lands here, because those sections carry no
 *    fit-profile block of their own. So does any product template without the
 *    block.
 *
 * The shared panel is excluded from the first query so it can only ever be
 * reached as the fallback, whatever it happens to be nested inside.
 *
 * @param {HTMLFormElement} form
 * @returns {(HTMLElement & {lineProperties: () => object, openAndAwait: () => Promise<boolean>}) | null}
 */
function panelFor(form) {
  const scope = form.closest('.shopify-section, dialog, body');
  return resolveFitPanel({
    own: scope?.querySelector('fit-profile:not([data-fit-shared])') ?? null,
    shared: document.querySelector('fit-profile[data-fit-shared]'),
  });
}

/**
 * Reads off the DOM what `panelIsVisible` in fit-profile-units.js decides on:
 * the panel's own content element and every element above it, each reduced to
 * `hidden` and — for a `<dialog>` — whether it is open. The RULE is over there,
 * where `node --test` reaches it; this is only the walk.
 *
 * It starts at `[data-ref="panel"]` rather than at the `<fit-profile>` element,
 * and that is the load-bearing half. `<fit-profile>` is never itself `hidden`;
 * the element `first` mode paints `hidden`, and `openAndAwait()` unhides, is
 * the panel INSIDE it. Starting at the custom element would report a hidden
 * panel as visible and `pass` straight over the case this exists to catch — a
 * host-page panel that flipped to `carried` from a quick-add save (Addendum 7)
 * and so carries the carry-over sentence and the gift checkbox while still
 * being `hidden`, because nothing ever opened it.
 *
 * The walk continues past the panel because `hidden` on an ancestor hides it
 * just as completely, and because the closed `<dialog>` this addendum is about
 * is an ancestor, not the panel.
 *
 * @param {Element | null} panel
 * @returns {import('@theme/fit-profile-units').PanelLayer[] | null}
 */
function panelAncestry(panel) {
  if (!panel) return null;
  const layers = [];
  for (let node = refIn(panel, 'panel') ?? panel; node instanceof HTMLElement; node = node.parentElement) {
    layers.push({
      hidden: node.hidden,
      dialogOpen: node instanceof HTMLDialogElement ? node.open : null,
    });
  }
  return layers;
}

/**
 * Stamps this line's fit properties onto the form as hidden inputs.
 *
 * The panel assembles its own line — `lineProperties()` knows whether these
 * measurements are the shared profile, an override for this piece, or the
 * "ask me" sentinel, and returns `_fit_source` accordingly. Falling back to
 * the cart profile is only for a page with no panel at all. Do not re-derive
 * the provenance here; there is one place that decides it.
 *
 * Each key reuses its own hidden input rather than appending a new one, or
 * repeated adds from the same form accumulate duplicates — the cart would
 * receive `properties[Height]` several times over, and the customer's fifth
 * add would carry their first add's numbers.
 *
 * @param {HTMLFormElement} form
 * @param {{lineProperties: () => object} | null} panel The panel this line
 *   answers to — the same one the gate was opened against. Passed in rather
 *   than looked up again, because a second lookup is a second chance to pick a
 *   different element.
 */
function injectProperties(form, panel) {
  const properties = panel ? panel.lineProperties() : fitProfile.properties('inherited');

  // A page with no panel and no profile has nothing to say about this line.
  // Shipping `_fit_source` beside a blank Height and Weight would claim a
  // provenance for measurements that are not there, so send nothing at all
  // and let the sale proceed ungated, exactly as `requireProfile()` does.
  const usable = properties.Height && properties.Weight ? properties : {};

  for (const key of FIT_PROPERTY_KEYS) {
    const existing = form.querySelector(`input[name="properties[${key}]"][data-fit-profile]`);
    const value = usable[key];
    if (!value) {
      // Removed, not blanked: a stale value left on a hidden input is a
      // production measurement from a previous add on this same form.
      existing?.remove();
      continue;
    }
    let input = existing;
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.dataset.fitProfile = 'true';
      form.appendChild(input);
    }
    input.value = value;
  }
}

/**
 * Gates every product form on the page on a fit profile existing.
 *
 * ONE listener, on `window`, at the CAPTURE phase. Both halves of that matter
 * and neither is obvious, so: Horizon does not attach
 * `on:submit="/handleSubmit"` — written on `<product-form-component>` in
 * `theme/blocks/buy-buttons.liquid` — to that element. `component.js`'s
 * `registerEventListeners()` installs ONE delegated listener per event type on
 * `document`, with `{ capture: true }`, and from there calls the method named
 * by the `on:<event>` attribute of the closest `Component`. For `submit` that
 * is `ProductFormComponent.handleSubmit`, which is what POSTs `/cart/add.js`.
 *
 * The capture path is `window -> document -> … -> form`, so a capture listener
 * on `document` runs BEFORE any listener on the form, whatever order the two
 * were registered in. This gate used to be a capture listener on the form
 * itself, under a docblock asserting that Horizon's was a bubble-phase
 * listener on an ancestor. It is not, and that one wrong sentence was the
 * whole defect: by the time the gate's `stopImmediatePropagation()` ran,
 * `handleSubmit` had already fired the add. The gate never held once —
 * deterministically, not as a race — and a garment cut to order could reach
 * the cart with no measurements on it, which is the single thing this feature
 * exists to prevent.
 *
 * `window` is the one node ahead of `document` on the capture path, so binding
 * here wins unconditionally: not by script order, not by module evaluation
 * order, not by which Horizon component upgrades first. Winning on order was
 * never available anyway — `registerEventListeners()` runs from the first
 * `Component.connectedCallback()` on the page, long before this module, whose
 * `<script type="module">` sits inside the panel snippet in the body, is
 * evaluated at all.
 *
 * Any future listener in this file that has to beat Horizon's belongs on
 * `window` at capture for the same reason — never on `document`, never on the
 * element. `scripts/test_fit_profile_gate_capture.py` pins both halves of
 * that: where Horizon delegates from, and where this registers.
 *
 * Being one listener rather than one per form also retires what used to need a
 * `boundForms` WeakSet, a `bindAll` sweep and a `MutationObserver`: a form
 * Horizon re-renders into place is covered the moment it exists, because
 * nothing was ever bound to the form it replaced. There is no DOM to wait for
 * either, so no `DOMContentLoaded` branch — `window` is there before this file
 * is parsed.
 *
 * Deliberately NOT done by disabling or hiding the button. A disabled button
 * is what the removed hijack block did, and it cost the theme its own AJAX
 * cart: every add became a full-page POST to /cart. Letting the native
 * button stay native means the customer keeps Horizon's cart drawer, its
 * error handling and its section re-rendering, and this file only decides
 * whether the submit it already dispatched may proceed.
 */
window.addEventListener(
  'submit',
  async (event) => {
    // Every form on the page reaches this listener — search, discount code,
    // newsletter, comments — so for anything that is not a product form it has
    // to be inert, which means returning before it touches the event at all.
    // A submit event's target IS the form and `closest` matches the element
    // itself, so this filters and resolves the form in one step.
    const target = event.target;
    if (!(target instanceof Element)) return;
    const form = /** @type {HTMLFormElement | null} */ (target.closest('product-form-component form'));
    if (!form) return;

    // One lookup, used for the gate AND the properties. See `panelFor`.
    const panel = panelFor(form);

    const action = decideSubmitAction({
      cleared: clearedForms.has(form),
      gating: gatingForms.has(form),
      panelGating: panel ? gatingPanels.has(panel) : false,
      panelVisible: panelIsVisible(panelAncestry(panel)),
      profileSet: fitProfile.isSet(),
    });

    // The re-submit issued at the foot of this handler. Its properties are
    // already on the form; Horizon must see this one untouched.
    if (action === 'consume-clearance') {
      clearedForms.delete(form);
      return;
    }

    // A click arriving while a panel is still open — either this same form's
    // (`gating`) or another form's, against the panel this one answers to
    // (`panelGating`). Drop it outright: do not await, do not queue a second
    // continuation, or one Save click adds a line twice — or, across forms,
    // adds a product the customer never saw added.
    if (action === 'suppress') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (action === 'pass') {
      injectProperties(form, panel);
      return; // let Horizon handle it
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    gatingForms.add(form);
    if (panel) gatingPanels.add(panel);
    try {
      const saved = await requireProfile(panel);
      if (!saved) return; // panel torn down, or the customer backed out

      injectProperties(form, panel);
      clearedForms.add(form);
      try {
        form.requestSubmit();
      } finally {
        // `requestSubmit()` dispatches `submit` synchronously, so the
        // clearance has already been consumed above. `finally` is for the
        // paths where it was not: a throw, or a form detached mid-await. A
        // clearance left behind would wave a later genuine click straight
        // through without re-checking the profile or re-injecting, and it
        // would carry whatever properties happened to be on the form.
        clearedForms.delete(form);
      }
    } finally {
      // Settles the gate whichever way the flow ended — saved, backed out,
      // or thrown — so the next click is gated afresh rather than
      // suppressed forever. Both keys, or a panel left marked would wedge
      // every OTHER form that answers to it, which on a collection page is
      // every card on the grid.
      gatingForms.delete(form);
      if (panel) gatingPanels.delete(panel);
    }
  },
  { capture: true }
);

// ---------------------------------------------------------------------------
// Accelerated checkout
// ---------------------------------------------------------------------------

/**
 * Reveals or hides every gated element (`theme/blocks/accelerated-checkout.liquid`).
 *
 * Shop Pay and the other accelerated buttons cannot be gated the way the add
 * paths above are: they skip the cart entirely, so there is no later moment at
 * which a fit profile could be collected or reconciled onto the order. The only
 * safe treatment is to not offer the shortcut until the profile exists.
 *
 * Hidden, not disabled. A disabled Shop Pay button is a dead end that explains
 * nothing and invites a support email; an absent one asks no questions, and the
 * ordinary "Add to cart" beside it still works and still opens the panel. The
 * button appears the moment the profile is saved, which is what makes this
 * a delay rather than a removal.
 *
 * Liquid paints the initial state from `cart.attributes['Fit height']`, so a
 * cold page is correct before this file has even parsed. This runs it once at
 * startup anyway, to cover a page served from cache with a cart that has since
 * been given a profile.
 */
function renderFitGates() {
  for (const element of document.querySelectorAll('[data-fit-gate]')) {
    /** @type {HTMLElement} */ (element).hidden = !fitProfile.isSet();
  }
}

renderFitGates();
fitProfile.subscribe(renderFitGates);

// ---------------------------------------------------------------------------
// The cart: editing the profile, and correcting the lines that inherited it
// ---------------------------------------------------------------------------

/**
 * The real cart, for `reconcileLines`. Injected rather than reached for, which
 * is what lets every rule in `fit-profile-reconcile.js` be tested under
 * `node --test` with no browser — see that file's tests.
 *
 * `Theme.routes` is asymmetric and this is where that bites: `cart_add_url`
 * already ends in `.js` (see `snippets/scripts.liquid`), `cart_url` and
 * `cart_change_url` do NOT. Appending `.js` to the wrong one fails silently —
 * `/cart/change` answers with a full HTML page, not JSON.
 *
 * @type {import('@theme/fit-profile-reconcile').ReconcileDeps}
 */
const cartDeps = {
  fetchCart: async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) throw new Error('Could not read the cart');
    return response.json();
  },
  changeLine: async (line, properties) => {
    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, properties }),
    });
    // Must throw, not return: `reconcileLines` reads a rejection as "this line
    // did not get written" and reports the line rather than moving on.
    if (!response.ok) throw new Error(`Could not update line ${line}`);
  },
};

/**
 * The class that reveals the partial-reconcile message, set on the ROOT
 * element.
 *
 * Not on the message itself, and not an attribute the message carries. The
 * message lives inside the cart section, and everything inside a cart section
 * is periodically thrown away and re-morphed from freshly fetched server HTML
 * — a quantity change does it, and so does the reconcile's own re-render. The
 * server has no idea a reconcile failed, so its copy of that markup is always
 * the resting state, and `morph`'s `copyAttributes` restores it. Anything
 * painted onto the element is therefore erased by the next render, whoever
 * starts it and whenever it lands.
 *
 * `<html>` is outside every section, so no morph reaches it. Visibility is
 * then derived in CSS (`cart-summary.liquid`) rather than painted, which
 * removes the race instead of trying to win it.
 */
const RECONCILE_OUTSTANDING_CLASS = 'fit-profile-reconcile-outstanding';

/** Whether the last reconcile left the cart part-corrected. */
let reconcileIncomplete = false;

/** Reveals or hides the "we couldn't update every piece" line in the cart. */
function renderReconcileMessage() {
  document.documentElement.classList.toggle(RECONCILE_OUTSTANDING_CLASS, reconcileIncomplete);
}

/**
 * The panel the cart's edit link opens.
 *
 * The shared one first — `quick-add-modal.liquid` renders it from
 * `layout/theme.liquid`, so on a cart page it is usually the only one there,
 * and it lives in its own `<dialog>`, which is what puts it on screen over a
 * page that has no panel of its own. Any other panel is the fallback, for a
 * cart drawer opened on a product page with the quick-add modal switched off.
 * @returns {FitProfileElement | null}
 */
function findProfilePanel() {
  const panel =
    document.querySelector('fit-profile[data-fit-shared]') || document.querySelector('fit-profile');
  return panel instanceof FitProfileElement ? panel : null;
}

/**
 * Re-renders every cart section on the page from the server, and waits for it.
 *
 * Done here rather than by dispatching a `CartUpdateEvent` and letting
 * `CartItemsComponent` react. That component's `#handleCartUpdate` starts
 * `sectionRenderer.renderSection(...)` and does NOT await it, so the caller
 * gets control back long before the new HTML has landed — which is how the
 * partial-reconcile message used to be painted and then wiped by the render it
 * had just asked for. Owning the render means "this function returned" and "the
 * cart on screen is what the server says" are the same moment.
 *
 * `renderSection` resolves after `morphSection` has run (its body reaches
 * `morph()` with nothing to await in between), so awaiting it is enough.
 *
 * `allSettled`, because one section failing to re-render must not stop another,
 * and because a failure here is a stale VIEW of a cart whose lines are already
 * written — it is not a reason to claim the write failed.
 */
async function refreshCartSections() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const id = /** @type {HTMLElement} */ (element).dataset.sectionId;
    if (id) ids.add(id);
  }
  await Promise.allSettled([...ids].map((id) => sectionRenderer.renderSection(id, { cache: false })));
}

/**
 * Rewrites the fit properties on every line that inherited the old profile,
 * then re-renders the cart from the server.
 *
 * The re-render is not cosmetic. After a partial reconcile some lines carry the
 * new measurements and some still carry the old ones, and the only truthful
 * account of which is which is the one the server gives — the cart's line list
 * prints each line's own `Height`/`Weight`. Reporting from what this function
 * believes it did would be reporting from the side that just failed.
 *
 * It is also unconditional. It does not depend on the reconcile having
 * succeeded, on a count lookup, or on anything else that might fail on its own:
 * a reconcile that wrote every line and then skipped the re-render leaves the
 * customer watching the panel close over a cart still showing pre-edit numbers,
 * with nothing to say the edit landed.
 *
 * No `CartUpdateEvent` is dispatched. The only listener with anything to update
 * is `cart-icon.js`, which reads `itemCount` off the event — and a reconcile
 * cannot change it: it writes properties, never quantities, and when changing a
 * line's properties merges it into another the merged line takes on the sum of
 * both quantities. Dispatching one would also restart exactly the un-awaited
 * second render this function exists to replace.
 *
 * `reconcileLines` never rejects, so the `catch` here is for a bug rather than
 * a network failure; it is written to fail closed all the same, because the
 * one thing that must not happen is a partial reconcile reported as a success.
 */
async function reconcileCartLines() {
  // Height and Weight only. `_fit_source` is provenance, and the reconcile
  // corrects measurements — see the `profile` parameter in
  // `fit-profile-reconcile.js` for why writing it would be wrong.
  const { Height, Weight } = fitProfile.properties('inherited');

  // Starts true and is only cleared by a reconcile that reported every line
  // done. Any other ending — a reported failure, or a throw this function is
  // not expecting — leaves it set.
  let incomplete = true;
  try {
    const result = await reconcileLines({ Height, Weight }, cartDeps);
    incomplete = result.failedAtLine !== null;
  } catch {
    incomplete = true;
  }
  reconcileIncomplete = incomplete;

  // Order does not matter to correctness here — the message is revealed by a
  // class on <html>, which no section render can reach — but the flag is set
  // first so that a render landing mid-flight already paints against the
  // settled answer.
  renderReconcileMessage();
  await refreshCartSections();
}

/** Guards against a second click queueing a second reconcile over the first. */
let profileEditInFlight = false;

/**
 * The cart's "Change your measurements" link.
 *
 * The reconcile runs only when the stored profile actually MOVED, or when the
 * last one did not finish. A save that re-enters the same numbers rewrites
 * nothing — which matters because every write here lands on a line a garment
 * will be cut from — but the outstanding case is the exception, and it has to
 * be: the message on screen says "try again", and re-opening this panel is the
 * only way the customer has to do that. Without it, trying again would appear
 * to do nothing unless they also changed a number.
 */
async function openProfileEditor() {
  if (profileEditInFlight) return;
  const panel = findProfilePanel();
  if (!panel) return;

  profileEditInFlight = true;
  try {
    const before = fitProfile.get();
    const saved = await panel.openForProfileEdit();
    if (!saved) return;

    const after = fitProfile.get();
    const moved = after.height !== before.height || after.weight !== before.weight;
    if (!moved && !reconcileIncomplete) return;

    await reconcileCartLines();
  } finally {
    profileEditInFlight = false;
  }
}

// Delegated, because the cart re-renders itself constantly — a listener bound
// to the link would be lost the first time a quantity changed. Nothing has to
// be re-bound or repainted after a render for the same reason the message is
// revealed from <html>: neither piece of state lives in the markup that gets
// replaced.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.closest('[data-fit-cart-edit] [data-ref="editButton"]')) return;
  event.preventDefault();
  openProfileEditor();
});
